import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { GAME_BY_ID } from "@/lib/games";
import { useActiveChild } from "@/lib/useActiveChild";

/**
 * Wrapper component that:
 * - Shows the game in a friendly frame with a "Sair" button
 * - Renders children with onFinish callback
 * - On finish, POSTs session to backend then displays result screen
 */
export default function GameShell({ gameId, title, children: render }) {
  const child = useActiveChild();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const game = GAME_BY_ID[gameId];

  useEffect(() => {
    if (!child) navigate("/play");
  }, [child, navigate]);

  const finish = async ({ score, metrics, duration_ms }) => {
    if (!child) return;
    setSaving(true);
    try {
      await api.post("/sessions", {
        child_id: child.id,
        game_id: gameId,
        score,
        metrics,
        duration_ms,
      });
      // refresh coins
      try {
        const { data } = await api.get(`/children/${child.id}`);
        localStorage.setItem("active_child", JSON.stringify(data));
      } catch {}
    } catch (e) {
      console.error(e);
    }
    setSaving(false);
    setResult({ score, metrics, duration_ms });
  };

  if (!child) return null;

  return (
    <div
      className="kid-mode min-h-screen relative bg-cover bg-center"
      style={{ backgroundImage: `url(${game.bg})` }}
      data-testid={`game-${gameId}`}
    >
      <div className="absolute inset-0 bg-black/30" />

      <header className="relative z-10 flex items-center justify-between p-4">
        <button
          onClick={() => navigate("/play/hub")}
          className="kid-btn bg-white/95 text-slate-700 text-sm"
          data-testid="game-exit"
        >
          ← Voltar
        </button>
        <div className="bg-white/90 px-4 py-2 rounded-full font-bold text-slate-800">
          {game.emoji} {title}
        </div>
        <div className="w-20" />
      </header>

      <main className="relative z-10 px-4 pb-6">
        {result ? (
          <ResultScreen
            gameId={gameId}
            result={result}
            saving={saving}
            onReplay={() => setResult(null)}
            onHome={() => navigate("/play/hub")}
          />
        ) : (
          <div className="max-w-4xl mx-auto">{render({ onFinish: finish, child })}</div>
        )}
      </main>
    </div>
  );
}

function ResultScreen({ gameId, result, saving, onReplay, onHome }) {
  const game = GAME_BY_ID[gameId];
  const m = result.metrics || {};
  return (
    <div className="max-w-xl mx-auto mt-6 kid-card text-center pop-in" data-testid="game-result">
      <div className="text-6xl">🎉</div>
      <h2 className="text-3xl mt-2">Boa jogada!</h2>
      <p className="text-slate-600 mt-1">
        Você terminou o jogo <strong>{game.name}</strong>.
      </p>
      <div className="grid grid-cols-2 gap-3 mt-6">
        <Stat label="Pontuação" value={result.score} />
        <Stat label="Tempo" value={`${(result.duration_ms / 1000).toFixed(1)}s`} />
        {m.hits !== undefined && <Stat label="Acertos" value={m.hits} />}
        {m.misses !== undefined && <Stat label="Erros" value={m.misses} />}
        {m.avg_reaction_ms && (
          <Stat label="Tempo de reação" value={`${Math.round(m.avg_reaction_ms)}ms`} />
        )}
        {m.moves !== undefined && <Stat label="Movimentos" value={m.moves} />}
      </div>
      {saving && <p className="text-sm text-slate-500 mt-4">Salvando…</p>}
      <div className="flex gap-3 justify-center mt-6">
        <button onClick={onReplay} className="kid-btn bg-amber-400 text-white" data-testid="result-replay">
          Jogar de novo
        </button>
        <button onClick={onHome} className="kid-btn bg-sky-500 text-white" data-testid="result-home">
          Voltar à ilha
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-sky-50 rounded-2xl p-4">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
    </div>
  );
}
